import { useState, useEffect } from 'react';
import api, { handleApiError } from '../services/api';
import { formatDateTime } from '../utils/formatters';
import { Filter, Search, ChevronLeft, ChevronRight, FileText, Clock } from 'lucide-react';

const actionBadgeClass = (action) => {
  if (action === 'create' || action === 'INSERT') return 'badge-success';
  if (action === 'update' || action === 'UPDATE') return 'badge-info';
  if (action === 'delete' || action === 'DELETE') return 'badge-danger';
  if (action === 'login' || action === 'LOGIN') return 'badge-warning';
  return 'badge-gray';
};

const modules = [
  { value: '', label: 'Semua Module' },
  { value: 'users', label: 'Users' },
  { value: 'products', label: 'Produk' },
  { value: 'sales', label: 'Penjualan' },
  { value: 'purchases', label: 'Pembelian' },
  { value: 'inventory', label: 'Persediaan' },
  { value: 'accounts', label: 'Akun' },
  { value: 'journals', label: 'Jurnal' },
  { value: 'settings', label: 'Pengaturan' },
  { value: 'auth', label: 'Autentikasi' },
];

const actions = [
  { value: '', label: 'Semua Aksi' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'login', label: 'Login' },
];

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    module: '',
    action: '',
    dateFrom: '',
    dateTo: '',
    search: '',
  });

  useEffect(() => {
    fetchLogs();
  }, [pagination.page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.module && { module: filters.module }),
        ...(filters.action && { action: filters.action }),
        ...(filters.dateFrom && { date_from: filters.dateFrom }),
        ...(filters.dateTo && { date_to: filters.dateTo }),
        ...(filters.search && { search: filters.search }),
      };
      const res = await api.get('/audit-logs', { params });
      if (res.data.success) {
        setLogs(res.data.data?.logs || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const resetFilter = () => {
    setFilters({ module: '', action: '', dateFrom: '', dateTo: '', search: '' });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Audit Log</h1>
        <p className="page-subtitle">Pantau semua aktivitas dan perubahan sistem</p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="label">Module</label>
              <select
                className="input"
                value={filters.module}
                onChange={(e) => setFilters({ ...filters, module: e.target.value })}
              >
                {modules.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Aksi</label>
              <select
                className="input"
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              >
                {actions.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Dari Tanggal</label>
              <input
                type="date"
                className="input"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Sampai Tanggal</label>
              <input
                type="date"
                className="input"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Pencarian</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className="input pl-9"
                  placeholder="Cari detail..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilter()}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={applyFilter} className="btn-primary btn-sm">
              <Filter className="h-4 w-4 mr-1" /> Filter
            </button>
            <button onClick={resetFilter} className="btn-secondary btn-sm">
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-40" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-48" />
                </div>
              ))}
            </div>
          </div>
        ) : logs.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada audit log yang tercatat.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      <div className="flex items-center"><Clock className="h-3.5 w-3.5 mr-1" /> Waktu</div>
                    </th>
                    <th>User</th>
                    <th>Aksi</th>
                    <th>Module</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td className="whitespace-nowrap">{formatDateTime(log.created_at)}</td>
                      <td>
                        <span className="font-medium text-gray-900 dark:text-white">{log.user_name || '-'}</span>
                      </td>
                      <td>
                        <span className={`badge ${actionBadgeClass(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td>
                        <span className="text-gray-600 dark:text-gray-300">{log.module || '-'}</span>
                      </td>
                      <td className="max-w-xs">
                        <p className="truncate text-gray-500 dark:text-gray-400 text-xs" title={log.details}>
                          {log.details || '-'}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page <= 1}
                    className="btn btn-sm btn-outline"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
                    Halaman {pagination.page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="btn btn-sm btn-outline"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}